// pages/SAPConfigLayer.jsx
// Lapis Config / SPRO — tampilan atas `sap_config_tr` + `sap_config_object`,
// hasil syncConfigTransport() dari E070C/E070/E07T/E071/E071K (backend:
// sapCustomizing.js). Dipasang sebagai view ketiga di SAPCustomizing.jsx.
//
// ⚠ ATURAN UTAMA HALAMAN INI — jangan disederhanakan:
// `key_count` dan `obj_count` NULLABLE, dan NULL berarti **tidak diketahui**,
// BUKAN nol. 68% baris objek ber-NULL (6.686 dari 9.815). Sebabnya struktural,
// bukan bug: `E071.OBJ_NAME` memuat nama view/view cluster (`V_T030H`, `VC_TVRO`)
// sedangkan `E071K.OBJNAME` memuat nama tabel fisik — keduanya tidak pernah
// cocok kecuali objtype `TABU`.
//
// Akibatnya untuk UI:
//   1. NULL DITULIS "tidak tercatat", bukan 0 dan bukan sel kosong. Sel kosong
//      ambigu (gagal muat? nol? belum sync?); 0 adalah kebohongan langsung yang
//      akan dibaca sebagai "tidak ada baris yang berubah".
//   2. SETIAP angka agregat yang berasal dari SUM() diberi penanda `~` bila ada
//      NULL di dalam populasinya, dan angka `*_unknown`-nya ditampilkan di
//      sebelahnya. SUM() di SQLite MENGABAIKAN NULL tanpa memberi tanda apa pun,
//      jadi hasilnya SELALU tampak lengkap. Endpoint sudah menyediakan pasangan
//      angka ini justru supaya tidak menyesatkan — membuangnya di UI
//      mengembalikan persis masalah yang perbaikan itu dibuat untuk menutup.
import { useState, useEffect, useCallback, useMemo } from 'react';
import TrObjectsModal from '../components/TrObjectsModal';
import SAPSproPanel from './SAPSproPanel';

// 2026-10-15: tombol tarik data langsung dari SAP khusus admin (backend juga menolak non-admin).
const isAdminUser = () => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } };


// ── Format angka ────────────────────────────────────────────────────────────
const NF = new Intl.NumberFormat('id-ID');
const fmtNum = (v) => (v == null ? null : NF.format(v));

// Nilai cacahan yang boleh NULL. `t` diteruskan agar teksnya ikut i18n.
// Dipakai untuk SEL TABEL — bukan untuk agregat (lihat fmtAgg).
function CountCell({ v, t }) {
  if (v == null) {
    return (
      <span title={t('sapCust.cfg.unknownHint')}
        style={{ color: '#b45309', fontStyle: 'italic', fontSize: 12 }}>
        {t('sapCust.cfg.unknown')}
      </span>
    );
  }
  return <span>{NF.format(v)}</span>;
}

// Agregat SUM() yang populasinya mengandung NULL. `unknown` > 0 → prefiks `~`,
// karena angkanya adalah batas BAWAH, bukan total.
function fmtAgg(sum, unknown) {
  const s = NF.format(sum || 0);
  return (unknown > 0) ? `~${s}` : s;
}

// ── Kartu statistik ─────────────────────────────────────────────────────────
function Card({ label, value, sub, color = 'rgb(var(--brand-600))', warn = false }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${warn ? '#fcd34d' : '#e2e8f0'}`,
      borderRadius: 12, padding: '0.9rem 1.1rem', minWidth: 140, flex: '1 1 140px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{label}</div>
      {sub != null && (
        <div style={{ fontSize: 11.5, color: warn ? '#b45309' : '#94a3b8', marginTop: 3 }}>{sub}</div>
      )}
    </div>
  );
}

// ── Sel status impor ────────────────────────────────────────────────────────
// EMPAT keadaan, dan membedakan keempatnya adalah inti kolom ini:
//   done    — sudah diimpor, RETCODE < 8
//   failed  — sudah diimpor tetapi RETCODE ≥ 8 (gagal/batal). BUKAN "sampai"
//             dalam arti yang berguna; menggabungkannya dengan `done` akan
//             menyembunyikan justru kasus yang paling perlu dilihat.
//   pending — dirilis, tetapi tidak ada catatan impor
//   null    — TIDAK DINILAI (belum dirilis atau dikecualikan). Ini yang paling
//             mudah salah: TR yang belum dirilis memang tidak punya catatan
//             impor, tetapi menampilkannya sebagai "belum sampai" akan menuduh
//             proses transport atas sesuatu yang belum pernah dikirim.
function ImportCell({ v, sid, trkorr, t }) {
  if (!v) return <span style={{ color: '#e2e8f0' }} title={t('sapCust.cfg.imp.naHint')}>·</span>;
  const M = {
    done:    { ch: '✓', color: '#0d9488', key: 'sapCust.cfg.imp.done' },
    failed:  { ch: '!', color: '#dc2626', key: 'sapCust.cfg.imp.failed' },
    pending: { ch: '—', color: '#b45309', key: 'sapCust.cfg.imp.pending' },
  }[v.status] || { ch: '?', color: '#94a3b8', key: 'sapCust.cfg.imp.pending' };
  // Tanggal dan RETCODE hanya di title: kolomnya harus tetap sempit, tapi
  // angkanya tetap terjangkau tanpa membuka apa pun.
  const bits = [t(M.key), sid];
  if (v.at) bits.push(v.at);
  if (v.rc) bits.push(`rc=${v.rc}`);
  // `via` HANYA bila catatan ditemukan lewat nomor TASK, bukan nomor TR itu
  // sendiri — backend selalu mengisinya, jadi tanpa perbandingan ini setiap sel
  // akan mengulang nomor yang sudah tertulis di kolom pertama. Ditampilkan saat
  // berbeda karena pengguna yang mencocokkan dengan STMS akan mencari nomor yang
  // tidak ada di sana.
  if (v.via && v.via !== trkorr) bits.push(`via ${v.via}`);
  return (
    <span title={bits.join(' · ')}
      style={{ color: M.color, fontWeight: 700, cursor: 'help' }}>{M.ch}</span>
  );
}

const TH = {
  padding: '0.5rem 0.65rem', textAlign: 'left', fontSize: 11.5, fontWeight: 700,
  color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
};
const TD = {
  padding: '0.42rem 0.65rem', fontSize: 12.5, color: '#1e293b',
  borderBottom: '1px solid #f1f5f9', verticalAlign: 'top',
};
const INP = {
  padding: '0.38rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1',
  fontSize: 12.5, minWidth: 0,
};

// Header kolom yang bisa diklik untuk mengurutkan.
//
// `on` sengaja WAJIB berupa kunci whitelist backend (CFG_SORT_TR / CFG_SORT_OBJ
// di sapCustomizing.js). Kunci yang tidak ada di whitelist akan DIABAIKAN backend
// secara senyap dan panahnya tetap tampil — pengguna melihat tanda urutan pada
// kolom yang sebenarnya tidak mengurutkan apa pun. Karena itu kolom yang tidak
// bisa diurutkan (mis. Komp. aplikasi di tab objek, yang dilekatkan setelah query
// berjalan) HARUS dibiarkan sebagai <th> biasa, bukan diberi `on` yang tidak sah.
function SortTH({ label, on, sort, dir, onSort, align = 'left' }) {
  const active = sort === on;
  return (
    <th style={{ ...TH, textAlign: align, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onSort(on)}
      title={label}>
      {label}
      <span style={{ marginLeft: 4, fontSize: 9,
        color: active ? 'rgb(var(--brand-700))' : '#cbd5e1' }}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}


// Alasan pengecualian — TR tersaring TETAP tercatat agar bisa diperiksa manusia,
// jadi label di sini harus menyebut SEBAB, bukan sekadar "dikecualikan".
//
// Label dicari lewat helper, BUKAN `t(key) || reason`: t() memulangkan KUNCINYA
// sendiri saat tidak ketemu (LanguageContext.js), sehingga `||` tidak pernah
// aktif dan alasan baru dari backend akan tampil sebagai literal
// `sapCust.cfg.exc.xxx` di layar. Perbandingan eksplisit terhadap key itulah
// satu-satunya cara mendeteksinya.
// E070.AS4DATE datang sebagai 'YYYYMMDD' tanpa pemisah, dan sering juga sebagai
// '' atau '00000000' untuk TR yang belum dirilis. Ketiganya harus jatuh ke
// keadaan "tidak ada tanggal", BUKAN dirender apa adanya: '00000000' yang lolos
// akan tampil sebagai tanggal yang terlihat sah.
// Sengaja TIDAK memakai new Date(): string ini tanpa zona waktu, dan mem-parse-nya
// lalu memformat ulang bisa menggeser satu hari tergantung zona peramban.
function fmtSapDate(v) {
  const s = String(v || '').trim();
  if (!/^\d{8}$/.test(s) || s === '00000000') return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

// Bagian ringkasan yang bisa dilipat.
//
// Keadaan lipat disimpan di localStorage, BUKAN state biasa: nilainya untuk
// pengguna justru pada bertahannya. Yang melipat ringkasan melakukannya karena
// ia datang ke halaman ini untuk tabelnya, dan itu berlaku besok juga.
//
// Kunci diberi awalan halaman agar tidak bertabrakan dengan halaman lain yang
// mungkin memakai pola sama nanti.
//
// try/catch WAJIB: localStorage melempar di mode privasi ketat sebagian
// peramban, dan kegagalan MENYIMPAN preferensi tampilan tidak boleh sampai
// merobohkan halaman yang isinya baik-baik saja.
function useCollapsed(key, initial = false) {
  const k = `sapCfgLayer.collapsed.${key}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { const v = localStorage.getItem(k); return v === null ? initial : v === '1'; }
    catch { return initial; }
  });
  const toggle = useCallback(() => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(k, next ? '1' : '0'); } catch { /* preferensi hilang, halaman tetap jalan */ }
      return next;
    });
  }, [k]);
  return [collapsed, toggle];
}

// Judul bagian yang bisa diklik. Panah menghadap BAWAH saat terbuka dan KANAN
// saat terlipat — arah yang sama dengan pohon berkas, sehingga tidak perlu
// dipelajari.
//
// Saat terlipat, ringkasan singkat isinya ditampilkan di sebelah judul. Bagian
// yang terlipat tanpa petunjuk isi memaksa pengguna membukanya hanya untuk
// mengingat apa yang ada di dalam — dan itu menghapus manfaat melipatnya.
function SectionBar({ label, hint, collapsed, onToggle }) {
  return (
    <button onClick={onToggle} title={label}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none', borderBottom: '1px solid #e2e8f0',
        padding: '0.35rem 0.1rem', marginBottom: 10, cursor: 'pointer' }}>
      <span style={{ color: '#0f766e', fontSize: 11, display: 'inline-block',
        transform: collapsed ? 'none' : 'rotate(90deg)', transition: 'transform .15s' }}>▶</span>
      <strong style={{ fontSize: 12, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </strong>
      {collapsed && hint && (
        <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 400 }}>· {hint}</span>
      )}
    </button>
  );
}

const EXC_COLOR = {
  sap_delivered: '#64748b',
  do_not_transport: '#b45309',
  client_export: '#7c3aed',
  duplicate_piece_list: '#0d9488',
  foreign_landscape: '#dc2626',
};

function excLabel(t, reason) {
  const key = `sapCust.cfg.exc.${reason}`;
  const v = t(key);
  return (v === key) ? reason : v;
}

export default function SAPConfigLayer({ api, t, connId, sproFocus }) {
  const [sum, setSum]         = useState(null);
  const [rows, setRows]       = useState([]);
  // Sistem hilir yang dinilai, datang dari respons /config. TIDAK di-hardcode
  // sebagai ['Q01','P01']: daftarnya diturunkan backend dari sap_connections per
  // landscape, jadi menuliskannya di sini akan salah begitu ada landscape lain
  // (JB/KL) atau sistem tambahan.
  const [impTargets, setImpTargets] = useState([]);
  // Apakah sap_dd_text sudah pernah diisi untuk koneksi ini. Membedakan
  // "objek ini tidak ada di kamus data" (fakta permanen untuk ~42% nama)
  // dari "sync deskripsi belum pernah jalan" — dua keadaan yang tampak
  // identik di sel kosong dan menuntun ke tindakan yang berbeda.
  const [ddReady, setDdReady] = useState(true);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');
  const [syncing, setSyncing] = useState(false);

  // `level` mencerminkan `?level=` di rute — SATU endpoint, bukan dua, supaya
  // logika filternya tidak bercabang menjadi dua salinan yang menyimpang.
  const [level, setLevel]     = useState('tr');
  // Tiga bagian ringkasan, masing-masing dengan tombol sendiri. Sengaja TIDAK
  // satu tombol untuk ketiganya: yang datang untuk angka KPI belum tentu butuh
  // panel tabel tersering, dan sebaliknya.
  // TR yang barisnya diklik. null = modal tertutup.
  const [modalTr, setModalTr] = useState(null);
  const [cStats, tStats] = useCollapsed('stats');
  const [cAggr,  tAggr]  = useCollapsed('aggr');
  const [cTbls,  tTbls]  = useCollapsed('tables');
  // Default `all` MENGIKUTI backend dengan sengaja: TR tersaring tetap tercatat
  // justru agar bisa diperiksa. Kalau UI diam-diam mengirim `none`, jumlah di
  // layar tidak akan pernah cocok dengan §3 handoff dan tampak seperti data hilang.
  // Default 'none' — SENGAJA BERUBAH dari 'all'. Dengan 'all', urutan bawaan
  // trkorr DESC menaruh nama berawalan Z/W/Y di depan, dan hampir seluruhnya
  // adalah 2.581 TR SAP-delivered: halaman pertama praktis tidak berisi informasi
  // yang berguna.
  //
  // ⚠ KONSEKUENSI YANG HARUS DITANGGUNG: jumlah baris di layar (7.906) kini tidak
  // cocok dengan kartu 'TR tercatat' (10.487) saat halaman dibuka, dan itu bisa
  // terbaca sebagai data hilang — persis alasan mengapa dulu 'all' yang dipilih.
  // Karena itu penyaringannya WAJIB dinyatakan eksplisit di sebelah jumlah baris,
  // bukan hanya tersirat dari isi dropdown.
  const [exc, setExc]         = useState('none');
  // Pengurutan dikerjakan BACKEND, bukan mengurutkan baris yang sedang tampil.
  // Mengurutkan di frontend akan mengurutkan 200 baris halaman ini saja, dan
  // pengguna akan membacanya sebagai "TR dengan kunci terbanyak" padahal artinya
  // "terbanyak di halaman ini" — angka benar, kesimpulan salah.
  const [sort, setSort]       = useState('');
  const [dir, setDir]         = useState('desc');
  const [fModule, setFModule] = useState('');
  // Filter TERPISAH dari fModule. Keduanya dimensi berbeda dan bisa dipakai
  // BERSAMAAN — 'module=BC (ABAP)' + 'module_app=PP' adalah pertanyaan yang sah
  // dan justru salah satu yang paling berguna: pekerjaan ABAP untuk domain PP.
  const [fModApp, setFModApp]   = useState('');
  const [fTrf, setFTrf]       = useState('');
  const [fStatus, setFStatus] = useState('');
  const [q, setQ]             = useState('');
  const [fObjname, setFObjname] = useState('');
  const [fObjtype, setFObjtype] = useState('');
  const [fTrkorr, setFTrkorr]   = useState('');
  const [limit]                 = useState(200);
  const [offset, setOffset]     = useState(0);

  // Agregat `by_module` dari backend di-GROUP BY (module, module_src), sehingga
  // satu modul bisa muncul lebih dari satu baris — `BC (ABAP)` muncul dua kali
  // (1382 lewat `[prefiks]` + 6 lewat `[prefiks abap]`) dan pembaca akan
  // menyimpulkan ada dua modul berbeda. Default halaman ini MENGGABUNGKAN per
  // `module` (hasilnya 1388, yang benar); toggle di bawah memecahnya kembali per
  // `module_src` karena jalur classifier baru memang sengaja dibuat bisa diaudit.
  const [splitSrc, setSplitSrc] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!connId) return;
    setErr('');
    try {
      const d = await api(`/api/sap/customizing/config-summary?connectionId=${connId}`);
      setSum(d || null);
    } catch (e) { setErr(e.message); setSum(null); }
  }, [api, connId]);

  const loadRows = useCallback(async () => {
    if (!connId) return;
    setLoading(true); setErr('');
    try {
      const p = new URLSearchParams({ connectionId: String(connId), level, excluded: exc,
        limit: String(limit), offset: String(offset) });
      if (sort) { p.set('sort', sort); p.set('dir', dir); }
      if (fModule) p.set('module', fModule);
      if (fModApp) p.set('module_app', fModApp);
      if (fTrf) p.set('trfunction', fTrf);
      if (level === 'tr') {
        if (fStatus) p.set('trstatus', fStatus);
        if (q.trim()) p.set('q', q.trim());
      } else {
        if (fObjname.trim()) p.set('objname', fObjname.trim());
        if (fObjtype.trim()) p.set('objtype', fObjtype.trim());
        if (fTrkorr.trim()) p.set('trkorr', fTrkorr.trim());
      }
      const d = await api(`/api/sap/customizing/config?${p.toString()}`);
      setRows(d.rows || []);
      setTotal(d.total || 0);
      // Hanya level 'tr' yang mengirim import_targets. Di level 'object' daftarnya
      // dikosongkan agar kolom status tidak tersisa dari tampilan sebelumnya.
      setImpTargets(level === 'tr' ? (d.import_targets || []) : []);
      setDdReady(d.dd_text_ready !== false);
    } catch (e) { setErr(e.message); setRows([]); setTotal(0); setImpTargets([]); }
    finally { setLoading(false); }
  }, [api, connId, level, exc, sort, dir, fModule, fModApp, fTrf, fStatus, q,
      fObjname, fObjtype, fTrkorr, limit, offset]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // Ganti level MERESET offset dan filter khusus level. Tanpa reset, filter
  // `objtype` yang tertinggal dari level objek akan diam-diam ikut ke level TR
  // (backend mengabaikannya) dan pengguna melihat kotak terisi yang tidak
  // berpengaruh apa pun — tampak seperti filter rusak.
  const switchLevel = useCallback((lv) => {
    setLevel(lv); setOffset(0);
    // Sort direset: kolom yang sah BERBEDA antar level, dan sort yang tertinggal
    // akan diabaikan backend tanpa pesan apa pun — panah di header akan menunjuk
    // kolom yang sebenarnya tidak sedang mengurutkan apa-apa.
    setSort(''); setDir('desc');
    setQ(''); setFStatus(''); setFObjname(''); setFObjtype(''); setFTrkorr('');
  }, []);

  // Klik kolom yang sama membalik arah; kolom baru mulai dari DESC. Offset direset
  // karena urutan baru membuat nomor halaman lama tidak berarti apa-apa.
  const onSort = useCallback((col) => {
    setOffset(0);
    if (sort === col) { setDir(d => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSort(col); setDir('desc');
  }, [sort]);

  const runSync = useCallback(async () => {
    if (!window.confirm(t('sapCust.cfg.syncConfirm'))) return;
    setSyncing(true); setErr(''); setMsg('');
    try {
      const r = await api(`/api/sap/customizing/sync-config?connectionId=${connId}`,
        { method: 'POST', body: '{}' });
      // Field-nya `rows` dan `objects` — lihat akhir syncConfigTransport di
      // sapCustomizing.js. Versi sebelumnya di sini membaca `r.tr ?? r.trCount`;
      // keduanya TIDAK ADA, jadi `|| 0` membuat pesan sukses melaporkan "0 TR"
      // padahal 10.487 TR tersimpan dengan benar. Kegagalan yang paling merusak
      // kepercayaan justru yang seperti ini: datanya betul, laporannya bohong,
      // dan tidak ada error di mana pun.
      setMsg(t('sapCust.cfg.syncDone', {
        tr:  NF.format((r && r.rows) || 0),
        obj: NF.format((r && r.objects) || 0),
      }));
      await loadSummary();
      await loadRows();
    } catch (e) { setErr(e.message); }
    finally { setSyncing(false); }
  }, [api, connId, t, loadSummary, loadRows]);

  const tot = sum?.totals || {};

  // Penggabungan by_module — lihat catatan pada `splitSrc`.
  const modRows = useMemo(() => {
    const src = sum?.by_module || [];
    if (splitSrc) return src.slice().sort((a, b) => b.tr - a.tr);
    const m = new Map();
    for (const r of src) {
      const k = r.module || '';
      const cur = m.get(k) || { module: k, tr: 0, obj: 0, objUnknown: false, srcs: new Set() };
      cur.tr += r.tr || 0;
      // `obj` bisa NULL untuk sebagian baris. Menjumlahkannya dengan `|| 0`
      // menyamakan "tidak diketahui" dengan nol — operator itulah yang sudah
      // dibuang dari `insTr` di backend karena alasan yang sama persis.
      if (r.obj == null) cur.objUnknown = true; else cur.obj += r.obj;
      if (r.module_src) cur.srcs.add(r.module_src);
      m.set(k, cur);
    }
    return [...m.values()].sort((a, b) => b.tr - a.tr);
  }, [sum, splitSrc]);

  const modTrSum = useMemo(() => modRows.reduce((n, r) => n + (r.tr || 0), 0), [modRows]);

  if (!connId) return null;

  return (
    <>
      {/* ── Batas informasi ──
          Sama alasannya dengan banner di view enhancement: pembaca tidak boleh
          menyimpulkan lebih jauh dari yang datanya dukung. Di sini batasnya ADA
          DUA dan keduanya mudah salah dibaca sebagai cakupan penuh. */}
      <div style={{ padding: '0.8rem 1rem', background: '#fffbeb', border: '1px solid #fde68a',
        borderRadius: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.55 }}>
          <strong>{t('sapCust.limitTitle')}</strong> {t('sapCust.cfg.limitBody')}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {isAdminUser() && (
        <button onClick={runSync} disabled={syncing}
          style={{ padding: '0.45rem 0.9rem', borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: '1px solid rgb(var(--brand-600))', background: syncing ? 'rgb(var(--brand-50))' : 'rgb(var(--brand-600))',
            color: syncing ? 'rgb(var(--brand-700))' : '#fff', cursor: syncing ? 'wait' : 'pointer' }}>
          {syncing ? t('sapCust.cfg.syncing') : t('sapCust.cfg.sync')}
        </button>)}
        {tot.snapshot_at && (
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {t('sapCust.lastSync')}: {String(tot.snapshot_at).replace('T', ' ').slice(0, 19)}
          </span>
        )}
      </div>

      {!!msg && (
        <div style={{ padding: '0.6rem 0.9rem', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 8, fontSize: 12.5, color: '#166534', marginBottom: 12 }}>{msg}</div>
      )}
      {!!err && (
        <div style={{ padding: '0.6rem 0.9rem', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, fontSize: 12.5, color: '#b91c1c', marginBottom: 12 }}>{err}</div>
      )}

      {/* ── Navigator SPRO (jalur IMG, pohon SPRO, compare antar sistem) — sapSpro.js ── */}
      <SAPSproPanel api={api} connId={connId} focus={sproFocus} />

      {/* ── Statistik ── */}
      {sum && (
        <SectionBar label={t('sapCust.cfg.secStats')} collapsed={cStats} onToggle={tStats}
          hint={t('sapCust.cfg.secStatsHint', { tr: fmtNum(tot.tr), obj: fmtNum(tot.object_rows) })} />
      )}
      {sum && !cStats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12, marginBottom: 16 }}>
          <Card label={t('sapCust.cfg.stat.tr')} value={fmtNum(tot.tr)} />
          <Card label={t('sapCust.cfg.stat.analyzed')} value={fmtNum(tot.analyzed)}
            sub={t('sapCust.cfg.stat.analyzedSub', { n: fmtNum(tot.tr - tot.analyzed) })}
            color="#0d9488" />
          <Card label={t('sapCust.cfg.stat.unreleased')} value={fmtNum(tot.unreleased)} color="#b45309" />
          <Card label={t('sapCust.cfg.stat.tables')} value={fmtNum(tot.tables)} color="#7c3aed" />
          <Card label={t('sapCust.cfg.stat.objectRows')} value={fmtNum(tot.object_rows)} color="#475569" />
          {/* Kartu PALING mudah disalahbaca di halaman ini: `keys` hanya
              menjumlahkan baris yang cacahnya DIKETAHUI. Karena itu ia diberi
              `~` dan sub-label yang menyebut berapa banyak yang TIDAK masuk
              hitungan — bukan sebagai catatan kaki, melainkan di kartu itu sendiri. */}
          <Card label={t('sapCust.cfg.stat.keys')}
            value={fmtAgg(tot.keys, tot.keys_unknown_objects)}
            sub={tot.keys_unknown_objects > 0
              ? t('sapCust.cfg.stat.keysSub', {
                  n: fmtNum(tot.keys_unknown_objects),
                  pct: tot.object_rows
                    ? Math.round((tot.keys_unknown_objects / tot.object_rows) * 100) : 0,
                })
              : null}
            warn={tot.keys_unknown_objects > 0} color="rgb(var(--brand-700))" />
          <Card label={t('sapCust.cfg.stat.heavy')} value={fmtNum(tot.heavy)}
            sub={t('sapCust.cfg.stat.heavySub')} color="#dc2626" />
          {/* Kartu status impor, satu per sistem hilir. Menampilkan `pending`
              sebagai angka utama karena itulah yang perlu ditindak; `done` sudah
              tersirat dari sisanya.

              ⚠ Sub-label WAJIB menyebut jumlah baris catatan impor. Sebelum
              19-08, sap_transport_import hanya berisi 173 baris untuk P01 dan
              angka "belum sampai" terbaca 7.181 (97,6%) — terlihat seperti
              temuan transport yang sah, padahal artinya TPALOG belum pernah
              ditanyai. Tanpa `rows` di kartu, kekeliruan itu tidak bisa
              dibedakan dari keadaan sebenarnya oleh siapa pun yang membaca. */}
          {(sum.import_status || []).map(s => {
            // Ambang 20%: di atas ini, `pending` lebih mungkin masalah cakupan
            // sinkron daripada masalah transport. Angkanya kasar dan memang
            // hanya untuk memicu peringatan visual, bukan untuk disimpulkan.
            const suspect = s.candidates > 0 && (s.pending / s.candidates) > 0.2;
            return (
              <Card key={s.sid}
                label={t('sapCust.cfg.stat.notArrived', { sid: s.sid })}
                value={fmtNum(s.pending)}
                sub={t('sapCust.cfg.stat.notArrivedSub', {
                  failed: fmtNum(s.failed),
                  rows: fmtNum(s.rows),
                  cand: fmtNum(s.candidates),
                })}
                warn={suspect || s.failed > 0}
                color={s.pending > 0 ? '#b45309' : '#0d9488'} />
            );
          })}
        </div>
      )}

      {/* ── Agregat modul / jenis transport / pengecualian ── */}
      {sum && (
        <SectionBar label={t('sapCust.cfg.secAggr')} collapsed={cAggr} onToggle={tAggr}
          hint={t('sapCust.cfg.secAggrHint')} />
      )}
      {sum && !cAggr && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 8, gap: 8 }}>
              <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.byModule')}</strong>
              <label style={{ fontSize: 11.5, color: '#64748b', display: 'flex', alignItems: 'center',
                gap: 5, cursor: 'pointer' }} title={t('sapCust.cfg.splitSrcHint')}>
                <input type="checkbox" checked={splitSrc} onChange={e => setSplitSrc(e.target.checked)} />
                {t('sapCust.cfg.splitSrc')}
              </label>
            </div>
            <div style={{ maxHeight: 260, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>{t('sapCust.cfg.col.module')}</th>
                  {splitSrc && <th style={TH}>{t('sapCust.cfg.col.moduleSrc')}</th>}
                  <th style={{ ...TH, textAlign: 'right' }}>TR</th>
                </tr></thead>
                <tbody>
                  {modRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...TD, cursor: 'pointer', color: 'rgb(var(--brand-700))' }}
                        onClick={() => { setFModule(r.module || ''); setOffset(0); }}>
                        {r.module || t('sapCust.cfg.noModule')}
                        {!splitSrc && r.srcs && r.srcs.size > 1 && (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}> ({r.srcs.size})</span>
                        )}
                      </td>
                      {splitSrc && <td style={{ ...TD, color: '#64748b', fontSize: 11.5 }}>{r.module_src || '—'}</td>}
                      <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                {/* Jumlah kolom TR HARUS sama dengan `analyzed` (by_module hanya
                    mencakup TR ber-`excluded_reason = ''`). Ditampilkan supaya
                    penggabungan di atas bisa dibuktikan, bukan dipercaya. */}
                <tfoot><tr>
                  <td style={{ ...TD, fontWeight: 700 }} colSpan={splitSrc ? 2 : 1}>
                    {t('sapCust.cfg.total')}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700,
                    color: modTrSum === tot.analyzed ? '#166534' : '#b91c1c' }}>
                    {NF.format(modTrSum)}
                  </td>
                </tr></tfoot>
              </table>
            </div>
          </div>

          {/* Panel komponen aplikasi. Kolom `campuran` BUKAN hiasan: 15% TR
              menyentuh lebih dari satu komponen, dan angka `tr` di sini hanya
              menghitung yang DOMINAN. Tanpa kolom itu, pembaca akan menyimpulkan
              setiap TR milik tepat satu domain. */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.byModuleApp')}</strong>
            <p style={{ fontSize: 11.5, color: '#64748b', margin: '6px 0 8px', lineHeight: 1.5 }}>
              {t('sapCust.cfg.byModuleAppHint')}
            </p>
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>{t('sapCust.cfg.col.moduleApp')}</th>
                  <th style={{ ...TH, textAlign: 'right' }}>TR</th>
                  <th style={{ ...TH, textAlign: 'right' }}>{t('sapCust.cfg.col.mixed')}</th>
                </tr></thead>
                <tbody>
                  {(sum.by_module_app || []).map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...TD, cursor: 'pointer', color: 'rgb(var(--brand-700))' }}
                        onClick={() => { setFModApp(r.module_app || ''); setOffset(0); }}>
                        {r.module_app}
                      </td>
                      <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                      <td style={{ ...TD, textAlign: 'right', fontSize: 11.5,
                        color: r.mixed > 0 ? '#b45309' : '#94a3b8' }}>
                        {r.mixed > 0 ? NF.format(r.mixed) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* TR tanpa komponen TIDAK muncul di GROUP BY mana pun, jadi tanpa
                angka ini jumlah baris di atas tidak akan pernah cocok dengan
                `analyzed` dan itu terbaca sebagai data hilang.
                
                DUA SEBAB, DIPISAH. Versi pertama menampilkan satu angka gabungan
                dengan penjelasan "rantai TADIR / paket ZD01" — dan itu salah untuk
                99,7% kasusnya: 4.187 dari 4.198 TR sama sekali tidak memuat objek
                customizing (umumnya workbench berisi program ABAP), sementara ZD01
                hanya menjelaskan 11. Satu kalimat tidak bisa menerangkan keduanya
                tanpa menyesatkan salah satunya. */}
            {sum.module_app_no_objects > 0 && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: '#64748b', lineHeight: 1.5 }}>
                {t('sapCust.cfg.modAppNoObj', { n: NF.format(sum.module_app_no_objects) })}
              </div>
            )}
            {sum.module_app_unreachable > 0 && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: '#b45309', lineHeight: 1.5 }}>
                {t('sapCust.cfg.modAppUnreach', { n: NF.format(sum.module_app_unreachable) })}
              </div>
            )}
          </div>

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.byTrfunction')}</strong>
            <div style={{ maxHeight: 260, overflow: 'auto', marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>{t('sapCust.cfg.col.trfunction')}</th>
                  <th style={{ ...TH, textAlign: 'right' }}>TR</th>
                  <th style={{ ...TH, textAlign: 'right' }}>{t('sapCust.cfg.col.analyzed')}</th>
                </tr></thead>
                <tbody>
                  {(sum.by_trfunction || []).map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...TD, cursor: 'pointer', color: 'rgb(var(--brand-700))' }}
                        onClick={() => { setFTrf(r.trfunction || ''); setOffset(0); }}>
                        {r.trfunction || '—'}
                      </td>
                      <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#64748b' }}>{NF.format(r.analyzed || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.8rem' }}>
            <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.excluded')}</strong>
            {/* Kalimat ini penting: "dikecualikan" di sini berarti TIDAK
                dianalisis isinya, BUKAN dibuang dari tabel. */}
            <p style={{ fontSize: 11.5, color: '#64748b', margin: '6px 0 8px', lineHeight: 1.5 }}>
              {t('sapCust.cfg.excludedHint')}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {(sum.excluded || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...TD, cursor: 'pointer' }}
                      onClick={() => { setExc('only'); setOffset(0); }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                        marginRight: 6, background: EXC_COLOR[r.excluded_reason] || '#94a3b8' }} />
                      {excLabel(t, r.excluded_reason)}
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Dua daftar tabel, BERDAMPINGAN dan sengaja tidak digabung ──
          Keduanya menjawab pertanyaan berbeda dan sama-sama benar:
            kiri  — apa yang TR DAFTARKAN (E071.OBJ_NAME). Untuk objtype non-TABU
                    ini nama VIEW / VIEW CLUSTER, dan cacah kuncinya tidak
                    diketahui karena nama view tidak pernah cocok ke E071K.
            kanan — tabel FISIK yang baris datanya benar-benar berubah
                    (E071K.OBJNAME), dengan cacah yang selalu terukur.
          Menggabungkannya akan membuat satu hal muncul dua kali dengan angka
          berbeda tanpa penjelasan — VC_T001L di kiri tanpa angka, T001L di kanan
          dengan angka. Berdampingan, perbedaannya justru yang mengajarkan. */}
      {sum && (!!(sum.top_tables || []).length || !!(sum.top_key_tables || []).length) && (
        <SectionBar label={t('sapCust.cfg.secTables')} collapsed={cTbls} onToggle={tTbls}
          hint={t('sapCust.cfg.secTablesHint')} />
      )}
      {sum && !cTbls && (!!(sum.top_tables || []).length || !!(sum.top_key_tables || []).length) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 12, marginBottom: 16 }}>

        {!!(sum.top_tables || []).length && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: '0.8rem' }}>
          <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.topTables')}</strong>
          <p style={{ fontSize: 11.5, color: '#64748b', margin: '6px 0 0', lineHeight: 1.5 }}>
            {t('sapCust.cfg.topTablesHint')}
          </p>
          <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={TH}>{t('sapCust.cfg.col.objname')}</th>
                <th style={TH}>{t('sapCust.cfg.col.objtype')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>TR</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('sapCust.cfg.col.keys')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('sapCust.cfg.col.known')}</th>
              </tr></thead>
              <tbody>
                {sum.top_tables.map((r, i) => {
                  const unknown = (r.row_count || 0) - (r.known || 0);
                  return (
                    <tr key={i}>
                      <td style={{ ...TD, fontFamily: 'monospace', cursor: 'pointer', color: 'rgb(var(--brand-700))' }}
                        onClick={() => { switchLevel('object'); setFObjname(r.objname || ''); }}>
                        {r.objname}
                      </td>
                      <td style={{ ...TD, color: '#64748b' }}>{r.objtype}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                      {/* `known === 0` berarti SELURUH barisnya tidak diketahui —
                          menampilkan "0" di sini persis kesalahan yang dulu
                          membuat dua pernyataan berbeda tercetak identik. */}
                      <td style={{ ...TD, textAlign: 'right' }}>
                        {r.known === 0
                          ? <CountCell v={null} t={t} />
                          : fmtAgg(r.keys, unknown)}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', fontSize: 11.5,
                        color: unknown > 0 ? '#b45309' : '#64748b' }}>
                        {NF.format(r.known || 0)} / {NF.format(r.row_count || 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Panel tabel FISIK — sisi E071K */}
        {!!(sum.top_key_tables || []).length && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: '0.8rem' }}>
          <strong style={{ fontSize: 12.5, color: '#334155' }}>{t('sapCust.cfg.keyTables')}</strong>
          <p style={{ fontSize: 11.5, color: '#64748b', margin: '6px 0 0', lineHeight: 1.5 }}>
            {t('sapCust.cfg.keyTablesHint')}
          </p>
          {/* Cakupan dinyatakan EKSPLISIT. Tanpa angka ini, daftar 25 teratas
              terbaca seolah itulah seluruhnya — padahal 1.390 tabel terlibat. */}
          <p style={{ fontSize: 11.5, color: '#0d9488', margin: '4px 0 0', fontWeight: 600 }}>
            {t('sapCust.cfg.keyTablesTotal', {
              tables: NF.format((sum.key_tables && sum.key_tables.tables) || 0),
              keys: NF.format((sum.key_tables && sum.key_tables.keys) || 0),
            })}
          </p>
          <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={TH}>{t('sapCust.cfg.col.tabname')}</th>
                <th style={{ ...TH, textAlign: 'right' }}>TR</th>
                <th style={{ ...TH, textAlign: 'right' }}>{t('sapCust.cfg.col.keys')}</th>
              </tr></thead>
              <tbody>
                {sum.top_key_tables.map((r, i) => (
                  <tr key={i}>
                    {/* TIDAK bisa diklik untuk memfilter, dan itu disengaja:
                        filter objname bekerja atas sap_config_object yang berisi
                        nama VIEW, sehingga mengklik T001L di sini akan memberi nol
                        baris — terbaca sebagai "tidak ada data" padahal yang salah
                        justru tabel tempat mencarinya. */}
                    <td style={{ ...TD, fontFamily: 'monospace' }}>{r.tabname}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>{NF.format(r.tr || 0)}</td>
                    {/* Selalu angka pasti — row_count tidak nullable, jadi tidak
                        ada keadaan "tidak diketahui" di kolom ini. */}
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 600, color: '#0d9488' }}>
                      {NF.format(r.keys || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        </div>
      )}

      {/* ── Pemilih level + filter ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['tr', t('sapCust.cfg.levelTr')], ['object', t('sapCust.cfg.levelObject')]].map(([k, label]) => (
          <button key={k} onClick={() => switchLevel(k)}
            style={{ padding: '0.4rem 0.8rem', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              cursor: 'pointer',
              border: level === k ? '1px solid rgb(var(--brand-600))' : '1px solid #cbd5e1',
              background: level === k ? 'rgb(var(--brand-50))' : '#fff',
              color: level === k ? 'rgb(var(--brand-700))' : '#475569' }}>
            {label}
          </button>
        ))}
        <span style={{ fontSize: 12, color: '#64748b', marginLeft: 4 }}>
          {loading ? t('sapCust.cfg.loading') : t('sapCust.cfg.rowCount', { n: NF.format(total) })}
        </span>
        {/* WAJIB ada selama default exc bukan 'all'. Jumlah baris di layar tidak
            akan cocok dengan kartu 'TR tercatat', dan tanpa kalimat ini selisih
            itu terbaca sebagai data hilang, bukan sebagai penyaringan yang
            sengaja. Sengaja BUKAN warna peringatan: ini keadaan normal. */}
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sapCust.cfg.sortHint')}</span>
        {exc !== 'all' && !loading && (
          <span style={{ fontSize: 11.5, color: '#0d9488', background: '#f0fdfa',
            border: '1px solid #99f6e4', borderRadius: 6, padding: '0.2rem 0.45rem' }}>
            {exc === 'none' ? t('sapCust.cfg.filterNoteNone') : t('sapCust.cfg.filterNoteOnly')}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...INP, width: 150 }} placeholder={t('sapCust.cfg.col.module')}
          value={fModule} onChange={e => { setFModule(e.target.value); setOffset(0); }} />
        <input style={{ ...INP, width: 140 }} placeholder={t('sapCust.cfg.col.moduleApp')}
          value={fModApp} onChange={e => { setFModApp(e.target.value); setOffset(0); }} />
        <input style={{ ...INP, width: 90 }} placeholder={t('sapCust.cfg.col.trfunction')}
          value={fTrf} onChange={e => { setFTrf(e.target.value); setOffset(0); }} />
        {level === 'tr' ? (
          <>
            <input style={{ ...INP, width: 90 }} placeholder={t('sapCust.cfg.col.trstatus')}
              value={fStatus} onChange={e => { setFStatus(e.target.value); setOffset(0); }} />
            <input style={{ ...INP, width: 220 }} placeholder={t('sapCust.cfg.searchTr')}
              value={q} onChange={e => { setQ(e.target.value); setOffset(0); }} />
          </>
        ) : (
          <>
            <input style={{ ...INP, width: 150 }} placeholder={t('sapCust.cfg.col.objname')}
              value={fObjname} onChange={e => { setFObjname(e.target.value); setOffset(0); }} />
            <input style={{ ...INP, width: 90 }} placeholder={t('sapCust.cfg.col.objtype')}
              value={fObjtype} onChange={e => { setFObjtype(e.target.value); setOffset(0); }} />
            <input style={{ ...INP, width: 150 }} placeholder="TRKORR"
              value={fTrkorr} onChange={e => { setFTrkorr(e.target.value); setOffset(0); }} />
          </>
        )}
        <select style={INP} value={exc} onChange={e => { setExc(e.target.value); setOffset(0); }}>
          <option value="all">{t('sapCust.cfg.excAll')}</option>
          <option value="none">{t('sapCust.cfg.excNone')}</option>
          <option value="only">{t('sapCust.cfg.excOnly')}</option>
        </select>
        <button onClick={() => {
          setFModule(''); setFModApp(''); setFTrf(''); setFStatus(''); setQ('');
          setFObjname(''); setFObjtype(''); setFTrkorr('');
          setExc('none'); setSort(''); setDir('desc'); setOffset(0);
        }} style={{ ...INP, cursor: 'pointer', background: '#fff', fontWeight: 600, color: '#475569' }}>
          {t('sapCust.cfg.reset')}
        </button>
      </div>

      {/* ── Tabel data ── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
        overflow: 'auto', maxHeight: 560 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
            {level === 'tr' ? (
              <tr>
                <SortTH label="TRKORR" on="trkorr" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.trfunction')} on="trfunction" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.trstatus')} on="trstatus" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.module')} on="module" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.moduleApp')} on="module_app" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.descr')} on="descr" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.objCount')} on="obj_count" sort={sort} dir={dir} onSort={onSort} align="right" />
                <SortTH label={t('sapCust.cfg.col.keys')} on="key_count" sort={sort} dir={dir} onSort={onSort} align="right" />
                {/* Sortable: released_on adalah kolom NYATA di sap_config_tr dan
                    sudah ada di CFG_SORT_TR, jadi pengurutan berlaku atas seluruh
                    populasi — tidak seperti kolom status impor di sebelahnya. */}
                <SortTH label={t('sapCust.cfg.col.releasedOn')} on="released_on" sort={sort} dir={dir} onSort={onSort} />
                {/* TIDAK sortable, dan itu disengaja: status impor dilekatkan di
                    JS setelah query berjalan (karena task_trkorr berisi daftar
                    dipisah koma yang tidak bisa dipecah di SQLite tanpa trik
                    rapuh), jadi backend tidak punya kolom untuk diurutkan.
                    Memberinya panah akan menjanjikan sesuatu yang tidak terjadi —
                    kegagalan yang sama sudah didokumentasikan untuk module_app
                    di tab objek. */}
                {impTargets.map(tg => (
                  <th key={tg.sid} style={{ ...TH, textAlign: 'center' }}
                    title={t('sapCust.cfg.imp.colHint', { sid: tg.sid, env: tg.environment || '' })}>
                    {tg.sid}
                  </th>
                ))}
                <SortTH label={t('sapCust.cfg.col.excluded')} on="excluded_reason" sort={sort} dir={dir} onSort={onSort} />
              </tr>
            ) : (
              <tr>
                <SortTH label={t('sapCust.cfg.col.objname')} on="objname" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label={t('sapCust.cfg.col.objtype')} on="objtype" sort={sort} dir={dir} onSort={onSort} />
                {/* Sortable, BERBEDA dari module_app di sebelahnya. Bedanya bukan
                    selera: module_app dilekatkan dari tabel cache lewat query
                    kedua dan tidak punya ekspresi yang bisa diurutkan di SQL,
                    sedangkan descr diurutkan backend lewat subquery terkorelasi
                    atas SELURUH populasi — sesuai janji header tabel ("sort the
                    whole data set, not just this page"). Objek tanpa entri kamus
                    selalu jatuh ke dasar, di kedua arah. */}
                <SortTH label={t('sapCust.cfg.col.ddText')} on="dd_text" sort={sort} dir={dir} onSort={onSort} />
                <SortTH label="TRKORR" on="trkorr" sort={sort} dir={dir} onSort={onSort} />
                {/* TIDAK sortable, dan itu disengaja: module_app dilekatkan lewat
                    query KEDUA setelah baris diambil, jadi backend tidak bisa
                    mengurutkan berdasarkan kolom ini. Memberinya panah akan
                    menjanjikan sesuatu yang tidak terjadi. */}
                <th style={TH}>{t('sapCust.cfg.col.moduleApp')}</th>
                <SortTH label={t('sapCust.cfg.col.keys')} on="key_count" sort={sort} dir={dir} onSort={onSort} align="right" />
              </tr>
            )}
          </thead>
          <tbody>
            {/* colSpan HARUS mengikuti jumlah kolom status impor. Bila dibiarkan
                9, baris "kosong" berhenti sebelum kolom terakhir dan tabel tampak
                rusak hanya pada keadaan kosong — cacat yang tidak terlihat selama
                pengujian dengan data.

                Komentar ini SENGAJA di luar tanda && (...): di dalamnya hanya
                boleh ada satu elemen, dan komentar JSX di sana menjadi objek
                kosong yang membuat esbuild gagal dengan pesan menyesatkan
                ("Unterminated regular expression" pada baris yang jauh). */}
            {!rows.length && !loading && (
              <tr><td colSpan={level === 'tr' ? 10 + impTargets.length : 6}
                style={{ ...TD, textAlign: 'center', color: '#94a3b8', padding: '1.4rem' }}>
                {t('sapCust.cfg.empty')}
              </td></tr>
            )}
            {level === 'tr' ? rows.map((r, i) => (
              <tr key={i} onClick={() => setModalTr(r)} title={t('sapCust.cfg.rowClickHint')}
                style={{ background: r.excluded_reason ? '#fcfcfd' : undefined, cursor: 'pointer' }}>
                <td style={{ ...TD, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {r.trkorr}
                  {r.is_heavy === 1 && (
                    <span title={t('sapCust.cfg.heavyHint')}
                      style={{ marginLeft: 5, fontSize: 10, color: '#dc2626', fontWeight: 700 }}>▲</span>
                  )}
                </td>
                <td style={TD}>{r.trfunction || '—'}</td>
                <td style={TD}>{r.trstatus || '—'}</td>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.module || '—'}</td>
                {/* Penanda campuran WAJIB. `module_app` hanya yang dominan; TR yang
                    menyentuh 19 komponen akan tampak identik dengan yang menyentuh
                    satu bila jumlahnya tidak ikut ditulis — persis kegagalan
                    sebangun dengan nol-palsu: angkanya benar, kesimpulannya salah.
                    Daftar penuh ada di title supaya tidak perlu drawer terpisah. */}
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                  {r.module_app
                    ? <>
                        {r.module_app}
                        {r.module_app_n > 1 && (
                          <span title={t('sapCust.cfg.mixedHint')
                                        .replace('{n}', String(r.module_app_n))
                                        .replace('{list}', String(r.module_app_all || ''))}
                            style={{ marginLeft: 4, fontSize: 10.5, fontWeight: 700,
                                     color: '#b45309', cursor: 'help' }}>
                            +{r.module_app_n - 1}
                          </span>
                        )}
                      </>
                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                </td>
                <td style={{ ...TD, maxWidth: 420 }}>{r.descr || '—'}</td>
                <td style={{ ...TD, textAlign: 'right' }}><CountCell v={r.obj_count} t={t} /></td>
                <td style={{ ...TD, textAlign: 'right' }}><CountCell v={r.key_count} t={t} /></td>
                {/* E070.AS4DATE — dan artinya BERGANTUNG STATUS:
                      trstatus R/N/O -> tanggal RILIS
                      trstatus D     -> tanggal terakhir DIUBAH, BUKAN tanggal buat
                    Karena itu kolomnya berlabel "Released", bukan "Created", dan
                    baris yang belum dirilis menyatakan dirinya "belum dirilis"
                    alih-alih memajang tanggal yang akan disalahbaca sebagai
                    tanggal pembuatan.
                    Tanggal buat SEBENARNYA ada di tabel SAP E070CREATE dan belum
                    ditarik. Jangan menambal kolom ini menjadi "Created" — sumber
                    datanya memang bukan itu. */}
                <td style={{ ...TD, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11.5 }}>
                  {fmtSapDate(r.released_on)
                    || <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontFamily: 'inherit' }}>
                         {String(r.trstatus || '').toUpperCase() === 'D'
                           ? t('sapCust.cfg.notReleased')
                           : '—'}
                       </span>}
                </td>
                {impTargets.map(tg => (
                  <td key={tg.sid} style={{ ...TD, textAlign: 'center' }}>
                    <ImportCell v={r.imports ? r.imports[tg.sid] : null}
                      sid={tg.sid} trkorr={r.trkorr} t={t} />
                  </td>
                ))}
                <td style={{ ...TD, fontSize: 11.5 }}>
                  {r.excluded_reason
                    ? <span style={{ color: EXC_COLOR[r.excluded_reason] || '#64748b' }}>
                        {excLabel(t, r.excluded_reason)}
                      </span>
                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                </td>
              </tr>
            )) : rows.map((r, i) => (
              <tr key={i}>
                <td style={{ ...TD, fontFamily: 'monospace' }}>{r.objname}</td>
                <td style={TD}>{r.objtype}</td>
                {/* descr === null berarti TIDAK ADA entri kamus — dinyatakan
                    eksplisit, tidak dibiarkan kosong. Sel kosong terbaca sebagai
                    "pengisian belum selesai" dan memicu sync ulang yang tidak
                    akan pernah mengubah apa pun.
                    lang ikut ditampilkan bila bukan 'E': VCLDIRT menyimpan
                    beberapa bahasa dan yang tersimpan bisa Jerman. Tanpa penanda,
                    teks Jerman terbaca seperti deskripsi Inggris yang aneh. */}
                <td style={{ ...TD, maxWidth: 340 }}
                  title={r.descr_src ? `${r.descr_src}${r.descr_kind ? ` · ${r.descr_kind}` : ''}` : ''}>
                  {r.descr
                    ? <>
                        {r.descr}
                        {r.descr_lang && r.descr_lang !== 'E' && (
                          <span style={{ marginLeft: 5, fontSize: 10, color: '#b45309', fontWeight: 700 }}>
                            {r.descr_lang}
                          </span>
                        )}
                      </>
                    : <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: 11.5 }}>
                        {ddReady ? t('sapCust.cfg.ddNone') : t('sapCust.cfg.ddNotSynced')}
                      </span>}
                </td>
                <td style={{ ...TD, fontFamily: 'monospace' }}>{r.trkorr}</td>
                {/* ps_posid mentah di title: itulah nilai SAP yang sebenarnya, dan
                    `module_app` hanyalah hasil pemetaan prefiksnya. Menyimpan
                    jejak ke sumber membuat kesalahan kamus bisa dilihat, bukan
                    ditebak. */}
                <td style={{ ...TD, whiteSpace: 'nowrap' }} title={r.ps_posid || ''}>
                  {r.module_app || <span style={{ color: '#cbd5e1' }}>—</span>}
                  {r.ps_posid && (
                    <span style={{ marginLeft: 5, fontSize: 10.5, color: '#94a3b8' }}>
                      {r.ps_posid}
                    </span>
                  )}
                </td>
                <td style={{ ...TD, textAlign: 'right' }}><CountCell v={r.key_count} t={t} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paging: offset saja, tanpa nomor halaman — plafon backend 5.000 baris
          dan populasinya 10.486 TR, jadi lompat halaman bukan kebutuhan nyata. */}
      {total > limit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(offset - limit, 0))}
            style={{ ...INP, cursor: offset === 0 ? 'not-allowed' : 'pointer', background: '#fff',
              opacity: offset === 0 ? 0.5 : 1 }}>‹</button>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {NF.format(offset + 1)}–{NF.format(Math.min(offset + limit, total))} / {NF.format(total)}
          </span>
          <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}
            style={{ ...INP, cursor: offset + limit >= total ? 'not-allowed' : 'pointer', background: '#fff',
              opacity: offset + limit >= total ? 0.5 : 1 }}>›</button>
        </div>
      )}

      {modalTr && (
        <TrObjectsModal api={api} connectionId={connId} tr={modalTr} onClose={() => setModalTr(null)} />
      )}
    </>
  );
}
